import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import { TeacherPhotoManager } from "@/components/TeacherPhotoManager";
import { MANAGE_ROLES, useAuth } from "@/contexts/AuthContext";
import { api, fmtBR, formatApiError, WEEKDAY_NAMES } from "@/lib/api";
import { EnrollmentStatusBadge } from "@/pages/Enrollments";

const EMPTY = { name: "", email: "", phone: "", notes: "", photo_url: "", public_bio: "", course_ids: [], active: true, user_id: "none" };

export default function Teachers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = MANAGE_ROLES.includes(user.role);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailEnrollments, setDetailEnrollments] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const openDetail = async (teacher) => {
    try {
      const { data } = await api.get("/enrollments", { params: { teacher_id: teacher.id } });
      setDetail(teacher);
      setDetailEnrollments(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = async () => {
    const requests = [api.get("/teachers"), api.get("/courses")];
    if (canManage) requests.push(api.get("/teachers/accounts"));
    const [t, c, a] = await Promise.all(requests);
    setTeachers(t.data);
    setCourses(c.data);
    setAccounts(canManage && a ? a.data : []);
  };

  useEffect(() => {
    load();
  }, []);

  const openDialog = (teacher) => {
    setEditing(teacher || null);
    setForm(
      teacher
        ? { name: teacher.name, email: teacher.email || "", phone: teacher.phone || "", notes: teacher.notes || "", photo_url: teacher.photo_url || "", public_bio: teacher.public_bio || "", course_ids: teacher.course_ids, active: teacher.active, user_id: teacher.user_id || "none" }
        : EMPTY
    );
    setError("");
    setDialogOpen(true);
  };

  const toggleCourse = (id) => {
    setForm((f) => ({
      ...f,
      course_ids: f.course_ids.includes(id) ? f.course_ids.filter((c) => c !== id) : [...f.course_ids, id],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.course_ids.length === 0) {
      setError("Selecione ao menos um curso.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, user_id: form.user_id === "none" ? null : form.user_id };
      if (editing) await api.put(`/teachers/${editing.id}`, payload);
      else await api.post("/teachers", payload);
      toast.success(editing ? "Professor atualizado." : "Professor cadastrado.");
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/teachers/${deleting.id}`);
      toast.success("Professor excluído.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Professores</h1>
          <p className="mt-1 text-sm text-muted-foreground">Corpo docente e os cursos que cada professor ministra.</p>
        </div>
        {canManage && (
          <Button onClick={() => openDialog(null)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="teacher-add-button">
            <Plus size={16} className="mr-2" /> Novo professor
          </Button>
        )}
      </div>

      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Cursos</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Contato</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {teachers.map((t) => (
              <TableRow key={t.id} className="transition-colors hover:bg-zinc-50" data-testid={`teacher-row-${t.name}`}>
                <TableCell className="font-medium">
                  {t.name}
                  {t.user_id && (
                    <Badge variant="secondary" className="ml-2 text-[10px]" data-testid={`teacher-account-badge-${t.name}`}>conta vinculada</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex max-w-xs flex-wrap gap-1">
                    {(t.course_names || []).map((n) => (
                      <Badge key={n} variant="secondary" className="text-xs">{n}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <p className="text-sm">{t.email || "—"}</p>
                  <p className="text-xs text-muted-foreground">{t.phone || ""}</p>
                </TableCell>
                <TableCell>
                  <Badge variant={t.active ? "default" : "secondary"} className={t.active ? "bg-primary text-primary-foreground" : ""}>
                    {t.active ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Alunos / Matrículas" onClick={() => openDetail(t)} data-testid={`teacher-detail-${t.name}`}>
                    <Eye size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" title="Disponibilidade" onClick={() => navigate(`/admin/disponibilidade?teacher=${t.id}`)} data-testid={`teacher-availability-${t.name}`}>
                    <Clock size={16} />
                  </Button>
                  {canManage && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openDialog(t)} data-testid={`teacher-edit-${t.name}`}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleting(t)} data-testid={`teacher-delete-${t.name}`}>
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="teacher-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar professor" : "Novo professor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="teacher-form-error">{error}</p>}
            <div className="space-y-2">
              <Label>Nome completo</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="teacher-name-input" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="teacher-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="teacher-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cursos que ministra</Label>
              <div className="grid grid-cols-2 gap-2 border border-border p-3">
                {courses.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.course_ids.includes(c.id)}
                      onCheckedChange={() => toggleCourse(c.id)}
                      data-testid={`teacher-course-${c.name}`}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="teacher-notes-input" />
            </div>
            <div className="space-y-2">
              <Label>Foto do professor</Label>
              <TeacherPhotoManager
                teacherId={editing?.id}
                photoUrl={form.photo_url}
                name={form.name}
                onChange={(url) => setForm((f) => ({ ...f, photo_url: url || "" }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Mini biografia pública (site futuro, opcional)</Label>
              <Textarea rows={2} value={form.public_bio} onChange={(e) => setForm({ ...form, public_bio: e.target.value })} data-testid="teacher-bio-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="teacher-active-switch" />
              <Label>Professor ativo</Label>
            </div>
            <div className="space-y-2">
              <Label>Conta de acesso (opcional)</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="teacher-account-select"><SelectValue placeholder="Sem conta vinculada" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem conta vinculada</SelectItem>
                  {accounts.map((a) => (
                    <SelectItem
                      key={a.id}
                      value={a.id}
                      disabled={!!a.linked_teacher_id && a.linked_teacher_id !== editing?.id}
                      data-testid={`teacher-account-option-${a.id}`}
                    >
                      {a.name} ({a.email}){a.linked_teacher_id && a.linked_teacher_id !== editing?.id ? " — já vinculada" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Vincula o professor à conta de usuário com perfil Professor (agenda, comunicados e frequência).
              </p>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="teacher-save-button">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="teacher-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.name} — Alunos / Matrículas</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-2" data-testid="teacher-enrollments-list">
              {detailEnrollments.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="teacher-enrollments-empty">
                  Nenhum aluno vinculado por matrícula.
                </p>
              )}
              {detailEnrollments.map((en) => (
                <div key={en.id} className="flex flex-wrap items-center gap-2 border border-border p-3" data-testid={`teacher-enrollment-${en.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {en.student_name}
                      {en.student_age != null && <span className="ml-1 text-xs text-muted-foreground">({en.student_age} anos)</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {en.course_name} · {en.modality_name} · {WEEKDAY_NAMES[en.weekday]} {en.start_time}–{en.end_time} · {en.room_name} · início {fmtBR(en.start_date)}
                    </p>
                  </div>
                  <EnrollmentStatusBadge status={en.status} />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Excluir professor"
        description={`Deseja excluir "${deleting?.name}"? As disponibilidades também serão removidas. Professores com aulas agendadas não podem ser excluídos.`}
        onConfirm={remove}
        testId="teacher-delete-confirm"
      />
    </div>
  );
}
